const HOUR_MINUTES = 60;
const DAY_MINUTES = 24 * HOUR_MINUTES;
const WEEK_MINUTES = 7 * DAY_MINUTES;
const MONTH_MINUTES = 30 * DAY_MINUTES;
const AOG_GROUNDED_THRESHOLD_MINUTES = 12 * HOUR_MINUTES; // No manual override: >= 12h on ground → AOG

function resolveOnGroundLabel(minutes) {
  // These labels are coarse buckets for the STATUS column only – not TAM.
  if (minutes >= 365 * DAY_MINUTES) return "1YR";
  if (minutes >= 6 * MONTH_MINUTES) return "6MO";
  if (minutes >= 5 * MONTH_MINUTES) return "5MO";
  if (minutes >= 4 * MONTH_MINUTES) return "4MO";
  if (minutes >= 3 * MONTH_MINUTES) return "3MO";
  if (minutes >= 2 * MONTH_MINUTES) return "2MO";
  if (minutes >= MONTH_MINUTES) return "1MO";
  if (minutes >= 3 * WEEK_MINUTES) return "3W";
  if (minutes >= 2 * WEEK_MINUTES) return "2W";
  if (minutes >= WEEK_MINUTES) return "1W";
  if (minutes >= 5 * DAY_MINUTES) return "5 DAYS";
  if (minutes >= 4 * DAY_MINUTES) return "4 DAYS";
  if (minutes >= 3 * DAY_MINUTES) return "3 DAYS";
  if (minutes >= 2 * DAY_MINUTES) return "2 DAYS";
  if (minutes >= DAY_MINUTES) return "1 DAY";
  return "ON GROUND > 24HRS";
}

export function normaliseTamStatus(value) {
  if (value === undefined || value === null) {
    return "";
  }
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  const upper = trimmed.toUpperCase();
  if (upper === "CHECK WITH DM") return "CHECK WITH FM";
  return upper;
}

// TAM values that should display as AOG when aircraft is on ground >= 12h (ME only)
const TAM_NON_OPERATIONAL_UPDATE_TO_AOG = new Set([
  "",
  "IN SERVICE",
  "IN STORAGE",
  "MAINTENANCE",
  "AWAITING OPERATOR FEEDBACK",
  "UNKNOWN",
]);

const ME_OWNER_CODE = "ME";

function isMeOwner(owner) {
  if (owner === undefined || owner === null) return false;
  return String(owner).trim().toUpperCase() === ME_OWNER_CODE;
}

function isMeFleetFlag(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null) return false;
  const s = String(value).trim().toLowerCase();
  return s === "true" || s === "1" || s === "yes" || s === "y";
}

/**
 * @param {object} opts
 * @param {boolean} [opts.isMeFleet] - When true, 12h-on-ground → AOG rule applies (Middle East fleet flag).
 * @param {string} [opts.owner] - Deprecated fallback: owner "ME" still applies 12h AOG if isMeFleet omitted.
 */
export function determineStatusCategory({
  groundedMinutes,
  isAirborne = false,
  hasRecentFlight = false,
  hasUpcomingFlight = false,
  hasMovementEvidence = false,
  tamStatus,
  hasManualOverride = false,
  owner = "",
  isMeFleet,
}) {
  const tamNormalised = normaliseTamStatus(tamStatus);
  const hasRecentActivity = Boolean(isAirborne || hasRecentFlight);
  const apply12hAogRule =
    isMeFleet !== undefined && isMeFleet !== null
      ? isMeFleetFlag(isMeFleet)
      : isMeOwner(owner);

  // Active aircraft should display as IN SERVICE only when we have movement evidence.
  // Scheduled-only data without any on/off (or manual baseline) is not enough.
  if (isAirborne || (hasUpcomingFlight && hasMovementEvidence)) {
    return { key: "in-service", label: "IN SERVICE" };
  }

  // ME only: when grounded >= 12h, these TAM statuses show AOG (same as cron/email). Others: no forced AOG.
  if (apply12hAogRule) {
    if (
      Number.isFinite(groundedMinutes) &&
      groundedMinutes >= AOG_GROUNDED_THRESHOLD_MINUTES &&
      TAM_NON_OPERATIONAL_UPDATE_TO_AOG.has(tamNormalised)
    ) {
      return { key: "aog", label: "AOG" };
    }
  }

  if (
    tamNormalised === "IN STORAGE" &&
    !hasRecentActivity &&
    (!Number.isFinite(groundedMinutes) || groundedMinutes < AOG_GROUNDED_THRESHOLD_MINUTES)
  ) {
    return { key: "storage", label: "IN STORAGE" };
  }

  if (Number.isFinite(groundedMinutes)) {
    if (!hasManualOverride) {
      // For ME: 12h+ → AOG. For non‑ME, stay IN SERVICE until 24h on ground.
      const nonMeGroundThreshold = DAY_MINUTES;
      const threshold = apply12hAogRule ? AOG_GROUNDED_THRESHOLD_MINUTES : nonMeGroundThreshold;
      if (groundedMinutes < threshold) {
        return { key: "in-service", label: "IN SERVICE" };
      }
      if (apply12hAogRule) {
        return { key: "aog", label: "AOG" };
      }
      const label = resolveOnGroundLabel(groundedMinutes);
      return { key: "on-ground", label };
    }
    if (groundedMinutes < DAY_MINUTES) {
      return { key: "in-service", label: "IN SERVICE" };
    }
    const label = resolveOnGroundLabel(groundedMinutes);
    return { key: "on-ground", label };
  }

  if (hasUpcomingFlight && !hasMovementEvidence) {
    return { key: "unknown", label: "UNKNOWN" };
  }

  return { key: "unknown", label: "UNKNOWN" };
}

export function shouldHighlightGrounded(groundedMinutes, categoryKey) {
  if (!Number.isFinite(groundedMinutes)) {
    return false;
  }
  if (categoryKey === "storage") {
    return false;
  }
  return groundedMinutes >= 3 * DAY_MINUTES;
}

export function inferManualStatusFromLastFlight(lastFlightIso, { now = new Date() } = {}) {
  if (!lastFlightIso) {
    return "";
  }
  const parsed = new Date(lastFlightIso);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  const diffMinutes = Math.max(0, Math.round((now.getTime() - parsed.getTime()) / 60000));
  const category = determineStatusCategory({
    groundedMinutes: diffMinutes,
    hasRecentFlight: diffMinutes < DAY_MINUTES,
  });
  return category?.label ? category.label.toUpperCase() : "";
}
